import isaSpecJson from "../../spec/am8_isa.json";

import { type BreakpointInfo, type CoreSnapshot, type Emulator } from "./emulator.js";
import { formatHexWord } from "./debugger-machine-state.js";

type IsaModeSpec = {
  group_nibble?: number;
  size: number;
};

type IsaGroupSpec = {
  group_nibble: number;
  modes: string[];
};

type IsaSpecialSpec = {
  code: number;
  mode: string;
};

type IsaSpec = {
  groups: IsaGroupSpec[];
  modes: Record<string, IsaModeSpec>;
  specials: IsaSpecialSpec[];
};

type DisassemblyLine = {
  address: number;
  activeInstruction: boolean;
  breakpoint: boolean;
  bytes: string[];
  bytesText: string;
  current: boolean;
  label: string;
  opcodeText: string;
  pcByteIndex: number | null;
};

const ISA_SPEC = isaSpecJson as IsaSpec;
const DISASSEMBLY_WINDOW_BYTES = 128;
const DISASSEMBLY_WINDOW_BEFORE_PC = 32;
const OPCODE_SIZES = buildOpcodeSizeTable();
const OPCODE_VALIDITY = buildOpcodeValidityTable();
const MAX_OPCODE_SIZE = OPCODE_SIZES.reduce((max, size) => Math.max(max, size), 1);

function buildOpcodeValidityTable(): Uint8Array {
  const table = new Uint8Array(256);

  for (const group of ISA_SPEC.groups) {
    for (const modeId of group.modes) {
      const mode = ISA_SPEC.modes[modeId];
      if (mode === undefined || mode.group_nibble === undefined) {
        continue;
      }
      table[group.group_nibble * 0x10 + mode.group_nibble] = 1;
    }
  }

  for (const special of ISA_SPEC.specials) {
    table[special.code] = 1;
  }

  return table;
}

export function clampDisassemblyBase(base: number, memorySize: number): number {
  const maxBase = Math.max(0, memorySize - DISASSEMBLY_WINDOW_BYTES);
  return Math.min(Math.max(0, base), maxBase);
}

export function deriveDisassemblyBase(anchor: number, memorySize: number): number {
  return clampDisassemblyBase(anchor - DISASSEMBLY_WINDOW_BEFORE_PC, memorySize);
}

function anchoredAlignmentCost(bytes: Uint8Array, startOffset: number, anchorOffset: number): number | null {
  let cost = 0;
  let cursor = startOffset;

  while (cursor < anchorOffset) {
    const byte = bytes[cursor];
    const size = opcodeSize(byte);
    const next = cursor + size;
    if (next > anchorOffset) {
      return null;
    }
    cost += OPCODE_VALIDITY[byte] === 0 ? 1 : 0;
    cursor = next;
  }

  return cursor === anchorOffset ? cost : null;
}

function localAlignmentCost(bytes: Uint8Array, startOffset: number): number {
  let cost = 0;
  let cursor = startOffset;
  let consumed = 0;

  while (cursor < bytes.length && consumed < DISASSEMBLY_WINDOW_BEFORE_PC) {
    const byte = bytes[cursor];
    const size = opcodeSize(byte);
    cost += OPCODE_VALIDITY[byte] === 0 ? 1 : 0;
    cursor += size;
    consumed += size;
  }

  return cost;
}

function chooseAlignedDisassemblyBase(
  bytes: Uint8Array,
  searchBase: number,
  desiredBase: number,
  anchor: number | null
): number {
  let bestBase = desiredBase;
  let bestCost = Number.POSITIVE_INFINITY;
  let bestDistance = Number.POSITIVE_INFINITY;
  let bestDirectionPenalty = Number.POSITIVE_INFINITY;
  const desiredOffset = desiredBase - searchBase;
  const anchorOffset = anchor === null ? null : anchor - searchBase;
  const startOffset = Math.max(0, desiredOffset - (MAX_OPCODE_SIZE - 1));
  const endOffset = Math.min(bytes.length - 1, desiredOffset + (MAX_OPCODE_SIZE - 1));

  for (let offset = startOffset; offset <= endOffset; offset += 1) {
    const candidateBase = searchBase + offset;
    const cost =
      anchorOffset !== null && offset <= anchorOffset
        ? anchoredAlignmentCost(bytes, offset, anchorOffset)
        : localAlignmentCost(bytes, offset);
    if (cost === null) {
      continue;
    }

    const distance = Math.abs(candidateBase - desiredBase);
    const directionPenalty = candidateBase > desiredBase ? 1 : 0;
    if (
      cost < bestCost ||
      (cost === bestCost && distance < bestDistance) ||
      (cost === bestCost && distance === bestDistance && directionPenalty < bestDirectionPenalty)
    ) {
      bestBase = candidateBase;
      bestCost = cost;
      bestDistance = distance;
      bestDirectionPenalty = directionPenalty;
    }
  }

  return bestBase;
}

function buildOpcodeSizeTable(): Uint8Array {
  const table = new Uint8Array(256);
  table.fill(1);

  for (const group of ISA_SPEC.groups) {
    for (const modeId of group.modes) {
      const mode = ISA_SPEC.modes[modeId];
      if (mode === undefined || mode.group_nibble === undefined) {
        continue;
      }
      table[group.group_nibble * 0x10 + mode.group_nibble] = mode.size;
    }
  }

  for (const special of ISA_SPEC.specials) {
    const mode = ISA_SPEC.modes[special.mode];
    if (mode === undefined) {
      continue;
    }
    table[special.code] = mode.size;
  }

  return table;
}

function opcodeSize(byte: number): number {
  const size = OPCODE_SIZES[byte & 0xff];
  return size > 0 ? size : 1;
}

function formatHexByte(value: number): string {
  return value.toString(16).toUpperCase().padStart(2, "0");
}

export function clearDisassembly(output: HTMLPreElement, message: string): void {
  output.textContent = message;
}

function buildDisassemblyLines(
  bytes: Uint8Array,
  base: number,
  rawText: string,
  instructionPc: number,
  currentPcByteIndex: number | null,
  highlightPc: boolean,
  breakpoints: ReadonlySet<number>
): DisassemblyLine[] {
  const lines: DisassemblyLine[] = [];
  const rawLines = rawText.trimEnd().split("\n");
  let offset = 0;
  let pendingLabel = "";

  for (const rawLine of rawLines) {
    const text = rawLine.trimEnd();
    if (text === "") {
      continue;
    }

    const trimmed = text.trimStart();
    if (trimmed.startsWith(".org ")) {
      continue;
    }

    const labelMatch = /^L_([0-9A-F]{4}):$/.exec(trimmed);
    if (labelMatch !== null) {
      pendingLabel = trimmed;
      continue;
    }

    const address = (base + offset) & 0xffff;
    const byteCount = offset < bytes.length ? opcodeSize(bytes[offset]) : 1;
    const opcodeBytes = bytes.slice(offset, Math.min(offset + byteCount, bytes.length));
    const byteStrings = Array.from(opcodeBytes, formatHexByte);
    const activeInstruction = address === instructionPc;
    lines.push({
      address,
      activeInstruction,
      bytes: byteStrings,
      bytesText: byteStrings.join(" "),
      breakpoint: breakpoints.has(address),
      current: highlightPc && activeInstruction,
      label: pendingLabel,
      opcodeText: trimmed,
      pcByteIndex: activeInstruction ? currentPcByteIndex : null
    });
    pendingLabel = "";

    if (offset < bytes.length) {
      offset += byteCount;
    }
  }

  return lines;
}

function renderDisassemblyLines(output: HTMLPreElement, lines: DisassemblyLine[]): void {
  output.textContent = "";
  const fragment = document.createDocumentFragment();
  const labelWidth = Math.max(0, ...lines.map((line) => line.label.length));
  const bytesWidth = Math.max(0, ...lines.map((line) => line.bytesText.length));

  for (const line of lines) {
    const lineElement = document.createElement("span");
    lineElement.className = "debugger-disassembly__line";
    if (line.current) {
      lineElement.classList.add("is-current");
    } else if (line.activeInstruction) {
      lineElement.classList.add("is-active-instruction");
    } else {
      lineElement.classList.add("is-context");
    }
    if (line.breakpoint) {
      lineElement.classList.add("has-breakpoint");
    }

    lineElement.append(
      document.createTextNode(
        `${line.current ? ">" : " "} ` +
          `${line.label.padEnd(labelWidth)} ` +
          `${formatHexWord(line.address)} `
      )
    );

    const bytesElement = document.createElement("span");
    bytesElement.className = "debugger-disassembly__bytes";
    line.bytes.forEach((byte, index) => {
      if (index > 0) {
        bytesElement.append(document.createTextNode(" "));
      }
      if (line.pcByteIndex === index) {
        const byteElement = document.createElement("span");
        byteElement.className = "debugger-disassembly__byte is-pc";
        byteElement.textContent = byte;
        bytesElement.append(byteElement);
      } else {
        bytesElement.append(document.createTextNode(byte));
      }
    });
    bytesElement.append(document.createTextNode(" ".repeat(bytesWidth - line.bytesText.length)));
    lineElement.append(bytesElement, document.createTextNode(` ${line.opcodeText}`));
    fragment.append(lineElement);
  }

  output.append(fragment);
}

export function renderDisassembly(
  output: HTMLPreElement,
  emulator: Emulator,
  snapshot: CoreSnapshot,
  breakpoints: BreakpointInfo[],
  followPc: boolean,
  manualBase: number | null,
  focusedAddress: number | null
): void {
  try {
    const desiredBase =
      followPc || manualBase === null
        ? deriveDisassemblyBase(snapshot.instrPc, emulator.getMemorySize())
        : clampDisassemblyBase(manualBase, emulator.getMemorySize());
    const searchBase = clampDisassemblyBase(desiredBase - (MAX_OPCODE_SIZE - 1), emulator.getMemorySize());
    const searchAvailable = Math.min(
      DISASSEMBLY_WINDOW_BYTES + (desiredBase - searchBase) + (MAX_OPCODE_SIZE - 1),
      emulator.getMemorySize() - searchBase
    );
    const searchBytes = emulator.copyMemory(searchBase, searchAvailable);
    const alignedBase = chooseAlignedDisassemblyBase(
      searchBytes,
      searchBase,
      desiredBase,
      followPc ? snapshot.instrPc : focusedAddress
    );
    const base = clampDisassemblyBase(alignedBase, emulator.getMemorySize());
    const available = Math.min(DISASSEMBLY_WINDOW_BYTES, emulator.getMemorySize() - base);
    const bytes = emulator.copyMemory(base, available);
    const raw = emulator.disassembleWorkbenchBytes(bytes, base);
    const instructionOffset = snapshot.instrPc - base;
    const instructionByteCount =
      instructionOffset >= 0 && instructionOffset < bytes.length ? opcodeSize(bytes[instructionOffset]) : 0;
    const currentPcByteIndex =
      snapshot.pc >= snapshot.instrPc && snapshot.pc < snapshot.instrPc + instructionByteCount
        ? snapshot.pc - snapshot.instrPc
        : null;
    const lines = buildDisassemblyLines(
      bytes,
      base,
      raw,
      snapshot.instrPc,
      currentPcByteIndex,
      snapshot.instrBoundary,
      new Set(
        breakpoints
          .map((breakpoint) => breakpoint.anchorAddress)
          .filter((address): address is number => address !== null)
      )
    );
    renderDisassemblyLines(output, lines);
  } catch (error) {
    clearDisassembly(output, error instanceof Error ? error.message : String(error));
  }
}
